import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

// ─── Color palette ─────────────────────────────────────────────────────────────
const PHASE_COLORS = {
    'Early-Race': '#3b82f6',
    'Mid-Race': '#22c55e',
    'Late-Race': '#f97316',
    'Last Spurt': '#ef4444',
};

const SLOPE_COLORS = {
    'Up': '#f87171',
    'Down': '#60a5fa',
};

// ─── Layout constants ───────────────────────────────────────────────────────────
const MARGIN = { top: 10, right: 12, bottom: 36, left: 12 };
const TRACK_H = 140;  // total height of the track area
const PHASE_Y = 0; const PHASE_H = 38;
const SLOPE_Y = 42; const SLOPE_H = 24;
const CORNER_Y = 72; const CORNER_H = 36;
const SPURT_Y = 0; const SPURT_H = TRACK_H;

/**
 * CourseMapTrack
 * D3-powered linear visualization of a racetrack course.
 *
 * Props:
 *   meters   {number}   Total distance of the course
 *   segments {object}   Structured segments from API
 *   width    {number}   Container width in px (responsive, from ResizeObserver)
 */
const CourseMapTrack = ({ meters, segments, width }) => {
    const svgRef = useRef(null);

    useEffect(() => {
        if (!segments || !meters || width <= 0 || !svgRef.current) return;

        const { Phases, Corners, Slopes, Straights, Spurt } = segments;

        // ── Derived dimensions ────────────────────────────────────────────────
        const innerW = width - MARGIN.left - MARGIN.right;
        const totalH = TRACK_H + MARGIN.top + MARGIN.bottom;

        // ── D3 scale: meters → pixels ─────────────────────────────────────────
        const xScale = d3.scaleLinear()
            .domain([0, meters])
            .range([0, innerW]);

        // ── Tooltip div (attached to body once) ──────────────────────────────
        let tooltip = d3.select('body').select('.course-map-tooltip');
        if (tooltip.empty()) {
            tooltip = d3.select('body')
                .append('div')
                .attr('class', 'course-map-tooltip')
                .style('position', 'absolute')
                .style('visibility', 'hidden')
                .style('background', 'rgba(17,24,39,0.95)')
                .style('color', '#e5e7eb')
                .style('padding', '6px 10px')
                .style('border-radius', '6px')
                .style('font-size', '11px')
                .style('font-family', 'monospace')
                .style('border', '1px solid #374151')
                .style('pointer-events', 'none')
                .style('z-index', '9999')
                .style('white-space', 'nowrap');
        }

        const showTooltip = (event, html) => {
            tooltip.style('visibility', 'visible').html(html);
        };
        const moveTooltip = (event) => {
            tooltip
                .style('top', (event.pageY - 36) + 'px')
                .style('left', (event.pageX + 12) + 'px');
        };
        const hideTooltip = () => {
            tooltip.style('visibility', 'hidden');
        };

        // ── Clear previous render ─────────────────────────────────────────────
        d3.select(svgRef.current).selectAll('*').remove();

        // ── Root SVG ─────────────────────────────────────────────────────────
        const svg = d3.select(svgRef.current)
            .attr('width', width)
            .attr('height', totalH)
            .attr('aria-label', 'Course track visualization');

        // ── Main group (shifted by margin) ────────────────────────────────────
        const g = svg.append('g')
            .attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

        // ── 1. Background base bar ────────────────────────────────────────────
        g.append('rect')
            .attr('x', 0).attr('y', 0)
            .attr('width', innerW).attr('height', TRACK_H)
            .attr('rx', 6)
            .attr('fill', '#111827');

        // ── 2. Spurt zone (full-height highlight, behind phases) ──────────────
        if (Spurt) {
            g.append('rect')
                .attr('x', xScale(Spurt.start))
                .attr('y', SPURT_Y)
                .attr('width', xScale(Spurt.end) - xScale(Spurt.start))
                .attr('height', SPURT_H)
                .attr('fill', '#a855f7')
                .attr('opacity', 0.12)
                .attr('rx', 3)
                .on('mouseover', (event) => showTooltip(event,
                    `<strong>Spurt Zone</strong><br>${Spurt.start}m → ${Spurt.end}m`
                ))
                .on('mousemove', moveTooltip)
                .on('mouseout', hideTooltip);
        }

        // ── 3. Phases ─────────────────────────────────────────────────────────
        if (Phases) {
            const phaseG = g.append('g').attr('class', 'phases');

            const phaseRects = phaseG.selectAll('rect')
                .data(Phases)
                .join('rect')
                .attr('x', d => xScale(d.start))
                .attr('y', PHASE_Y)
                .attr('height', PHASE_H)
                .attr('fill', d => PHASE_COLORS[d.type] || '#6b7280')
                .attr('opacity', 0.85)
                .attr('rx', d => (d.type === 'Early-Race' || d.type === 'Last Spurt') ? 4 : 0)
                // Animate width from 0 on enter
                .attr('width', 0);

            phaseRects.transition()
                .duration(700)
                .delay((d, i) => i * 80)
                .attr('width', d => Math.max(xScale(d.end) - xScale(d.start), 2));

            phaseRects
                .on('mouseover', (event, d) => showTooltip(event,
                    `<strong>${d.type}</strong><br>${d.start}m → ${d.end}m`
                ))
                .on('mousemove', moveTooltip)
                .on('mouseout', hideTooltip);

            // Phase labels (only when wide enough)
            phaseG.selectAll('text')
                .data(Phases)
                .join('text')
                .attr('x', d => xScale(d.start) + (xScale(d.end) - xScale(d.start)) / 2)
                .attr('y', PHASE_Y + (PHASE_H / 2))
                .attr('text-anchor', 'middle')
                .attr('dominant-baseline', 'middle')
                .attr('fill', 'white')
                .attr('font-size', 13)
                .attr('font-weight', 700)
                .attr('font-family', 'monospace')
                .attr('opacity', d => (xScale(d.end) - xScale(d.start)) > 80 ? 0.95 : 0)
                .text(d => d.type);
        }

        // ── 4. Slopes ─────────────────────────────────────────────────────────
        if (Slopes) {
            const slopeData = Slopes.filter(
                s => s.slope_type && s.slope_type !== 'None' && s.slope_value !== 0
            );

            const slopeRects = g.append('g').attr('class', 'slopes')
                .selectAll('rect')
                .data(slopeData)
                .join('rect')
                .attr('x', d => xScale(d.start))
                .attr('y', SLOPE_Y)
                .attr('height', SLOPE_H)
                .attr('fill', d => SLOPE_COLORS[d.slope_type] || '#9ca3af')
                .attr('opacity', 0.7)
                .attr('rx', 2)
                .attr('width', 0);

            slopeRects.transition()
                .duration(700)
                .delay((d, i) => 350 + i * 60)
                .attr('width', d => Math.max(xScale(d.end) - xScale(d.start), 2));

            slopeRects
                .on('mouseover', (event, d) => showTooltip(event,
                    `<strong>Slope ${d.slope_type}</strong> (${d.slope_value > 0 ? '+' : ''}${d.slope_value})<br>${d.start}m → ${d.end}m`
                ))
                .on('mousemove', moveTooltip)
                .on('mouseout', hideTooltip);
        }

        // ── 5. Corners ────────────────────────────────────────────────────────
        if (Corners) {
            const cornerData = Corners.filter(c => c.start !== null && c.end !== null);

            const cornerRects = g.append('g').attr('class', 'corners')
                .selectAll('rect')
                .data(cornerData)
                .join('rect')
                .attr('x', d => xScale(d.start))
                .attr('y', CORNER_Y)
                .attr('height', CORNER_H)
                .attr('fill', '#facc15')
                .attr('opacity', 0.55)
                .attr('rx', 2)
                .attr('width', 0);

            cornerRects.transition()
                .duration(700)
                .delay((d, i) => 500 + i * 60)
                .attr('width', d => Math.max(xScale(d.end) - xScale(d.start), 2));

            cornerRects
                .on('mouseover', (event, d) => showTooltip(event,
                    `<strong>Corner ${d.corner_number || ''}</strong><br>${d.start}m → ${d.end}m`
                ))
                .on('mousemove', moveTooltip)
                .on('mouseout', hideTooltip);
        }

        // ── 6. Straights (thin bottom strip) ──────────────────────────────────
        if (Straights) {
            g.append('g').attr('class', 'straights')
                .selectAll('rect')
                .data(Straights)
                .join('rect')
                .attr('x', d => xScale(d.start))
                .attr('y', TRACK_H - 10)
                .attr('height', 10)
                .attr('width', d => Math.max(xScale(d.end) - xScale(d.start), 1))
                .attr('fill', '#34d399')
                .attr('opacity', 0.75)
                .attr('rx', 2)
                .on('mouseover', (event, d) => showTooltip(event,
                    `<strong>Straight</strong><br>${d.start}m → ${d.end}m`
                ))
                .on('mousemove', moveTooltip)
                .on('mouseout', hideTooltip);
        }

        // ── 7. Outer border ───────────────────────────────────────────────────
        g.append('rect')
            .attr('x', 0).attr('y', 0)
            .attr('width', innerW).attr('height', TRACK_H)
            .attr('rx', 6)
            .attr('fill', 'none')
            .attr('stroke', '#1f2937')
            .attr('stroke-width', 1.5);

        // ── 8. D3 distance axis (auto-adjusting ticks, no overlap) ───────────
        const xAxis = d3.axisBottom(xScale)
            .ticks(Math.floor(innerW / 70))
            .tickFormat(d => d === 0 ? 'Start' : `${d}m`);

        const axisG = g.append('g')
            .attr('class', 'x-axis')
            .attr('transform', `translate(0, ${TRACK_H})`)
            .call(xAxis);

        // Style the axis to match the dark theme
        axisG.select('.domain').remove();
        axisG.selectAll('.tick line')
            .attr('stroke', '#374151')
            .attr('y1', -TRACK_H)
            .attr('y2', 0)
            .attr('stroke-dasharray', '2,3')
            .attr('opacity', 0.5);
        axisG.selectAll('.tick text')
            .attr('fill', '#6b7280')
            .attr('font-size', 9)
            .attr('font-family', 'monospace')
            .attr('dy', '1.2em');

        // Cleanup tooltip on unmount
        return () => {
            d3.select('body').select('.course-map-tooltip').remove();
        };

    }, [segments, meters, width]);

    return (
        <div className="w-full overflow-x-auto">
            <svg ref={svgRef} style={{ display: 'block', minWidth: 300 }} />
        </div>
    );
};

export default CourseMapTrack;
